import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import remarkGfm from "remark-gfm";

// Slugify heading text to a stable anchor id. Same rule browsers use
// for deep links: lowercase, non-alphanumerics -> single hyphen, trim.
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// react-markdown v9 passes `children` as an array; join to a string for
// slug generation.
function headingText(children: React.ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(headingText).join("");
  if (children && typeof children === "object" && "props" in children) {
    return headingText(
      (children as { props: { children?: React.ReactNode } }).props.children,
    );
  }
  return "";
}

export function Markdown({ children }: { children: string }) {
  return (
    <div className="max-w-3xl">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1
              id={slugify(headingText(children))}
              className="text-3xl font-semibold tracking-tight text-foreground mt-6 mb-4"
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              id={slugify(headingText(children))}
              className="text-2xl font-semibold tracking-tight text-foreground mt-6 mb-3"
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3
              id={slugify(headingText(children))}
              className="text-xl font-semibold text-foreground mt-4 mb-2"
            >
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-base font-semibold text-foreground mt-3 mb-2">
              {children}
            </h4>
          ),
          p: ({ children }) => (
            <p className="text-base leading-7 text-foreground mb-3">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-1 mb-3 text-foreground">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside space-y-1 mb-3 text-foreground">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-base leading-7">{children}</li>
          ),
          code: ({ className, children }) => {
            const isBlock = /language-/.test(className ?? "");
            if (isBlock) {
              return (
                <code className={`font-mono text-sm ${className ?? ""}`}>
                  {children}
                </code>
              );
            }
            return (
              <code className="font-mono text-sm rounded bg-muted/40 px-1.5 py-0.5">
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-muted/40 rounded p-3 overflow-x-auto text-sm mb-3">
              {children}
            </pre>
          ),
          a: ({ href, children }) => {
            if (href && href.startsWith("/") && !href.startsWith("//")) {
              return (
                <Link
                  to={href}
                  className="text-primary underline underline-offset-2 hover:no-underline"
                >
                  {children}
                </Link>
              );
            }
            if (href && href.startsWith("#")) {
              return (
                <a
                  href={href}
                  className="text-primary underline underline-offset-2 hover:no-underline"
                >
                  {children}
                </a>
              );
            }
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 hover:no-underline"
              >
                {children}
              </a>
            );
          },
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-primary/40 bg-primary/[0.04] px-3 py-2 my-3 text-foreground">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <table className="w-full border-collapse text-sm mb-3">
              {children}
            </table>
          ),
          th: ({ children }) => (
            <th className="text-left border-b border-border px-2 py-1 font-medium">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border-b border-border px-2 py-1 align-top">
              {children}
            </td>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

export { slugify };
